// ─── src/services/communityPostsService.js ──────────────────────────────────
// Community feed: posts, reactions, comments, moderation, storage upload.
// ─────────────────────────────────────────────────────────────────────────────

import { supabase } from '../lib/supabaseClient';

// ── Post type catalogue ───────────────────────────────────────────────────────
export const POST_TYPES = {
  venue_spotlight: { label: 'Venue Spotlight',    icon: '◈', color: '#d39a21' },
  behind_scenes:   { label: 'Behind the Scenes',  icon: '◉', color: '#60a5fa' },
  announcement:    { label: 'Announcement',        icon: '▷', color: '#34d399' },
  editorial:       { label: 'Editorial',           icon: '✦', color: '#d39a21' },
  before_after:    { label: 'Transformation',      icon: '⊞', color: '#a78bfa' },
  quote:           { label: 'Quote',               icon: '"', color: '#d39a21' },
  inspiration:     { label: 'Inspiration',         icon: '◇', color: '#f472b6' },
};

export const POST_TYPE_LIST = Object.entries(POST_TYPES).map(([key, val]) => ({ key, ...val }));

// ── Fetch published posts (paginated) ─────────────────────────────────────────
export async function fetchPublishedPosts({ page = 0, perPage = 12, type } = {}) {
  let q = supabase
    .from('community_posts')
    .select('*')
    .in('status', ['published', 'featured'])
    .order('is_featured', { ascending: false })
    .order('published_at', { ascending: false })
    .range(page * perPage, (page + 1) * perPage - 1);

  if (type && type !== 'all') q = q.eq('post_type', type);

  const { data, error } = await q;
  return { data: data || [], error };
}

// ── Fetch a single post ───────────────────────────────────────────────────────
export async function fetchPostById(id) {
  const { data, error } = await supabase
    .from('community_posts')
    .select('*')
    .eq('id', id)
    .single();
  return { data, error };
}

// ── Fetch vendor's own posts (portal) ─────────────────────────────────────────
export async function fetchVendorPosts(vendorId) {
  const { data, error } = await supabase
    .from('community_posts')
    .select('*')
    .eq('vendor_id', vendorId)
    .order('created_at', { ascending: false });
  return { data: data || [], error };
}

// ── Create post ───────────────────────────────────────────────────────────────
export async function createCommunityPost({
  vendorId, authorName, authorRole, authorAvatar, authorTier,
  postType = 'editorial', title, caption, bodyText,
  mediaUrls = [], coverImageUrl, tags = [], socialProjectId,
  isAdminPost = false,
  status = 'pending',       // admins may pass 'published' or 'featured' directly
  isFeatured = false,
} = {}) {
  const isPublished = status === 'published' || status === 'featured';
  const { data, error } = await supabase
    .from('community_posts')
    .insert({
      vendor_id:         vendorId || null,
      author_name:       authorName,
      author_role:       authorRole || null,
      author_avatar:     authorAvatar || null,
      author_tier:       authorTier || null,
      is_admin_post:     isAdminPost,
      post_type:         postType,
      title:             title?.trim() || null,
      caption:           caption?.trim() || null,
      body_text:         bodyText?.trim() || null,
      media_urls:        mediaUrls,
      cover_image_url:   coverImageUrl || mediaUrls[0] || null,
      social_project_id: socialProjectId || null,
      tags:              tags,
      status,
      is_featured:       isFeatured || status === 'featured',
      published_at:      isPublished ? new Date().toISOString() : null,
    })
    .select()
    .single();
  return { data, error };
}

// ── Update post ───────────────────────────────────────────────────────────────
export async function updateCommunityPost(id, updates) {
  const { data, error } = await supabase
    .from('community_posts')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  return { data, error };
}

// ── Delete post ───────────────────────────────────────────────────────────────
export async function deleteCommunityPost(id) {
  const { error } = await supabase
    .from('community_posts')
    .delete()
    .eq('id', id);
  return { error };
}

// ── Moderation ────────────────────────────────────────────────────────────────
export async function approvePost(id, { featured = false } = {}) {
  return updateCommunityPost(id, {
    status:       featured ? 'featured' : 'published',
    is_featured:  featured,
    published_at: new Date().toISOString(),
  });
}

export async function rejectPost(id, note = '') {
  return updateCommunityPost(id, {
    status:         'rejected',
    rejection_note: note,
  });
}

export async function featurePost(id) {
  return updateCommunityPost(id, { status: 'featured', is_featured: true });
}

export async function unfeaturePost(id) {
  return updateCommunityPost(id, { status: 'published', is_featured: false });
}

export async function fetchPendingPosts() {
  const { data, error } = await supabase
    .from('community_posts')
    .select('*')
    .eq('status', 'pending')
    .order('created_at', { ascending: true });
  return { data: data || [], error };
}

export async function fetchAllPosts({ status } = {}) {
  let q = supabase
    .from('community_posts')
    .select('*')
    .order('created_at', { ascending: false });
  if (status && status !== 'all') q = q.eq('status', status);
  const { data, error } = await q;
  return { data: data || [], error };
}

// ── Reactions ─────────────────────────────────────────────────────────────────
// sessionKey: a stable anonymous identifier stored in localStorage
export async function toggleReaction(postId, reactionType, sessionKey) {
  // Check if already reacted
  const { data: existing } = await supabase
    .from('community_post_reactions')
    .select('id')
    .eq('post_id', postId)
    .eq('reaction_type', reactionType)
    .eq('session_key', sessionKey)
    .maybeSingle();

  if (existing) {
    // Remove reaction
    await supabase.from('community_post_reactions').delete().eq('id', existing.id);
    const col = reactionType === 'like' ? 'likes_count' : 'saves_count';
    await supabase.rpc('decrement_community_post_count', { post_id: postId, col_name: col });
    return { reacted: false };
  } else {
    // Add reaction
    await supabase.from('community_post_reactions').insert({
      post_id:       postId,
      reaction_type: reactionType,
      session_key:   sessionKey,
    });
    const col = reactionType === 'like' ? 'likes_count' : 'saves_count';
    await supabase.rpc('increment_community_post_count', { post_id: postId, col_name: col });
    return { reacted: true };
  }
}

// Lightweight: just upsert + return optimistic state.
// Falls back gracefully if RPC doesn't exist yet (Phase 1).
export async function reactToPost(postId, reactionType, sessionKey) {
  try {
    return await toggleReaction(postId, reactionType, sessionKey);
  } catch {
    return { reacted: false, error: 'Reaction failed' };
  }
}

// Fetch which posts this session has reacted to
export async function fetchSessionReactions(sessionKey, postIds) {
  if (!postIds?.length) return { likes: [], saves: [] };
  const { data } = await supabase
    .from('community_post_reactions')
    .select('post_id, reaction_type')
    .eq('session_key', sessionKey)
    .in('post_id', postIds);
  const likes = (data || []).filter(r => r.reaction_type === 'like').map(r => r.post_id);
  const saves = (data || []).filter(r => r.reaction_type === 'save').map(r => r.post_id);
  return { likes, saves };
}

// ── Record view (fire-and-forget) ─────────────────────────────────────────────
export async function recordPostView(postId) {
  try {
    await supabase.rpc('increment_community_post_view', { post_id: postId });
  } catch { /* non-critical */ }
}

// ── Image upload to community-posts bucket ────────────────────────────────────
export async function uploadCommunityImage(file, postId) {
  const ext  = file.name.split('.').pop() || 'jpg';
  const path = `${postId || 'draft'}/${Date.now()}.${ext}`;

  const { data, error } = await supabase.storage
    .from('community-posts')
    .upload(path, file, { upsert: true, contentType: file.type });

  if (error) return { url: null, error };

  const { data: { publicUrl } } = supabase.storage
    .from('community-posts')
    .getPublicUrl(data.path);

  return { url: publicUrl, error: null };
}

// ── Session key helper ────────────────────────────────────────────────────────
export function getSessionKey() {
  try {
    const stored = localStorage.getItem('lwd_session_key');
    if (stored) return stored;
    const key = `sess_${Math.random().toString(36).slice(2)}_${Date.now()}`;
    localStorage.setItem('lwd_session_key', key);
    return key;
  } catch {
    return `sess_${Math.random().toString(36).slice(2)}`;
  }
}

// ── Time ago helper ───────────────────────────────────────────────────────────
export function timeAgo(ts) {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins  <  2)  return 'Just now';
  if (mins  < 60)  return `${mins}m ago`;
  if (hours < 24)  return `${hours}h ago`;
  if (days  < 7)   return `${days}d ago`;
  return new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}
